import type { CreditHoldDecisionInput } from '@chukua/common';
import { useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { decideCreditHold, type JobDetail } from '../../../jobs.api';

/**
 * A manager's decision on a credit hold (gitaumoses4/chukua#37) — same run/toast/409-reload
 * shape as `use-job-actions.ts`'s `run`, kept separate since it's one action with a decision
 * body rather than the six-action ladder.
 */
export function useJobCreditHold(reload: () => Promise<void>) {
  const [conflictError, setConflictError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  async function decide(job: Pick<JobDetail, 'id' | 'number'>, input: CreditHoldDecisionInput): Promise<void> {
    setConflictError(null);
    setSubmitting(true);
    try {
      await decideCreditHold(job.id, input);
      await reload();
      toast.success(input.decision === 'approved' ? `Job ${job.number} approved — confirmed` : `Job ${job.number} kept on credit hold`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        await reload();
        setConflictError("This job's state changed since the page loaded — showing the latest.");
      } else {
        toast.error(`Could not record the credit hold decision for job ${job.number} — try again.`);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return { conflictError, submitting, decide };
}
